import ImageWithFallback from 'gcs-common/design-system/ui/image-with-fallback';
import { ONLINE_STATUS } from 'gcs-common/helper/userStatusHelper';
import useRoundImageChecker from 'gcs-common/hooks/useRoundImageChecker';
import avatarPlaceHolder from 'gcs-common/img/user.svg';
import type { components } from 'gcs-common/types/openapi';
import { useEffect } from 'react';
import starIcon from '../../img/star_icon.svg';
import styles from './styles.module.scss';

const basicStatusStyle = {
  ONLINE: styles.aktivStatusIndicator,
  IDLE: styles.inkurzeVerfügbarStatusIndicator,
  OFFLINE: styles.inaktivStatusIndicator,
  UNKNOWN: styles.unknownStatusIndicator,
};

const dotStatusStyle = {
  ONLINE: styles.aktivStatusIndicatorDot,
  IDLE: styles.inkurzeVerfügbarStatusIndicatorDot,
  OFFLINE: styles.inaktivStatusIndicatorDot,
  UNKNOWN: styles.unknownStatusIndicatorDot,
};

const getStatusColor = ({
  type,
  isSalesSpace,
  isDot,
  isAgent,
  channelMemberCount,
}: {
  type: components['schemas']['OnlineStatus'];
  isSalesSpace: boolean;
  isDot: boolean;
  isAgent: boolean;
  channelMemberCount: number;
}) => {
  if (isDot) return styles.dotStatusIndicator;
  if (!isAgent && isSalesSpace) return styles.noStatusIndicator;
  if (!isSalesSpace && channelMemberCount > 2) return styles.noStatusIndicator;
  return basicStatusStyle[type];
};

const getImageStyle = (isSalesSpace: boolean, isAgent: boolean) => {
  if (isSalesSpace && !isAgent) return styles.salesSpaceAgentWrapper;
  return styles.normalImageWrapper;
};

const ImageWithStatus = ({
  isSalesSpace = false,
  channelMemberCount = 0,
  imageUrl,
  userStatus = { type: ONLINE_STATUS.UNKNOWN, text: 'Status unbekannt' },
  className = '',
  isSmall = false,
  isDot = false,
  isAgent = false,
  hasStar = false,
}: {
  isSalesSpace?: boolean;
  channelMemberCount?: number;
  imageUrl?: string;
  userStatus?: components['schemas']['ChatUserStatusDTO'];
  className?: string;
  isSmall?: boolean;
  isDot?: boolean;
  isAgent?: boolean;
  hasStar?: boolean;
}) => {
  const { type } = userStatus;
  const imageStatusStyle = getImageStyle(isSalesSpace, isAgent);
  const imageStatusColor = getStatusColor({
    type,
    isSalesSpace,
    isDot,
    isAgent,
    channelMemberCount,
  });

  const { isRound, setImageUrl } = useRoundImageChecker(imageUrl);

  useEffect(() => {
    setImageUrl(imageUrl);
  }, [imageUrl, setImageUrl]);

  return (
    <div className={`${styles.wrapper} ${className} ${isSmall ? styles.smallWrapper : ''}`}>
      <div
        className={`${styles.statusImageWrapper} 
          ${imageStatusStyle} 
          ${imageStatusColor} 
          ${isSmall ? styles.small : ''} 
          ${isRound ? styles.isRound : ''}`}
        data-testid="image-with-status"
        data-testuserstatus={type}
      >
        <ImageWithFallback
          className={styles.image}
          src={imageUrl}
          height={48}
          width={48}
          quality={85}
          fallback={<img className={styles.image} src={avatarPlaceHolder} alt={''} />}
          alt={''}
        />
      </div>

      {isDot && <div className={`${styles.statusDot} ${dotStatusStyle[type]}`} />}

      {channelMemberCount > 2 && !isSalesSpace && (
        <div className={styles.channelMemberCountBadge}>{channelMemberCount}</div>
      )}

      {hasStar && <img alt="Star Icon" className={`${styles.starIcon}`} src={starIcon} />}
    </div>
  );
};

export default ImageWithStatus;
